import { useCallback, useEffect, useState } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { UserActions } from 'BMapsSrc/cmds/UserCmd';
import { addMenuItem } from 'BMapsSrc/menu_cmds';
import { NativeCommandStateEvent } from 'BMapsSrc/VisualizerInterface';
import { SidePanelContainer } from './info_display/SidePanel';
import { CommandConsole } from './CommandConsole';
import {
    NativeCommandConsole, MainCanvasCommandLine, nativeCommandsSupported,
} from './NativeCommandConsole';
import useEventBrokerSubscription from './common/helpers/useEventBrokerSubscription';

export function CommandPanel({
    handleClose, open, longLived,
}) {
    return (
        <SidePanelContainer
            open={open}
            handleClose={handleClose}
            longLived={longLived}
            title="Commands"
            width="20rem"
            containerStyle={{
                background: '#DCF0E6',
                // set z index to 0 to override default
                // right side panel needs to be under the bfm_viewer so that tooltips are
                // displayed on top of it. this is not true for left side panel
                // which is on top of the bfm_viewer
                zIndex: 0,
            }}
        >
            <CommandModes open={open}>
                <CommandConsole open={open} />
            </CommandModes>
        </SidePanelContainer>
    );
}

/**
 * BMaps' own commands (`children`) and, when the 3D viewer backend has a command language,
 * a second tab with the viewer's command line. Both stay mounted so each keeps its output.
 *
 * Whether the tab exists is asked of the backend when the panel mounts or opens, and again
 * whenever the backend announces a change of its command line (NativeCommandStateEvent: the
 * backend became ready or failed). The backend is chosen once per page load today; a runtime
 * switch of backends would have to announce itself the same way.
 */
export function CommandModes({ open, commandLine=MainCanvasCommandLine, children }) {
    const [supported, setSupported] = useState(() => nativeCommandsSupported(commandLine));
    const [mode, setMode] = useState('bmaps');
    const [attribution, setAttribution] = useState(null);

    const recheck = useCallback((evtName, state) => {
        setSupported(nativeCommandsSupported(commandLine));
        let current = state;
        if (!current || typeof current !== 'object') {
            try {
                current = commandLine.state();
            } catch (ex) {
                current = null;
            }
        }
        setAttribution((current && current.attribution) || null);
    }, [commandLine]);
    useEventBrokerSubscription(NativeCommandStateEvent, recheck);
    useEffect(() => { if (open !== false) recheck(); }, [open, recheck]);

    if (!supported) return children;
    const showNative = mode === 'native';
    return (
        <div style={{
            display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden',
        }}
        >
            <Tabs
                value={mode}
                onChange={(e, value) => setMode(value)}
                variant="fullWidth"
                aria-label="Command input"
                sx={{ minHeight: 0, borderBottom: '1px solid lightgray', flex: '0 0 auto' }}
            >
                <Tab value="bmaps" label="BMaps" sx={{ minHeight: 0, padding: '0.4rem' }} />
                <Tab
                    value="native"
                    data-testid="native-command-tab"
                    sx={{ minHeight: 0, padding: '0.4rem', textTransform: 'none' }}
                    label={(
                        <span style={{ lineHeight: 1.2 }}>
                            <span style={{ textTransform: 'uppercase' }}>Viewer</span>
                            {!!attribution && (
                                <span style={{ display: 'block', fontSize: '0.65rem' }}>{attribution}</span>
                            )}
                        </span>
                    )}
                />
            </Tabs>
            <div style={{
                flex: '1 1 auto', minHeight: 0, display: showNative ? 'none' : 'flex', flexDirection: 'column',
            }}
            >
                {children}
            </div>
            <div style={{
                flex: '1 1 auto', minHeight: 0, display: showNative ? 'flex' : 'none', flexDirection: 'column',
            }}
            >
                <NativeCommandConsole commandLine={commandLine} active={showNative} />
            </div>
        </div>
    );
}

export function openCommandPanel() {
    UserActions.OpenSidePanel('right', {
        pageId: 'commandPanel',
        component: <CommandPanel keepOpenOnZap />,
        longLived: true,
    });
}

export function closeCommandPanel() {
    UserActions.OpenSidePanel('right', {
        pageId: 'commandPanel',
        close: true,
    });
}

export class CommandPanelLoader {
    static Init() {
        addMenuItem('Commands', 'Commands', openCommandPanel);
    }
}
